/**
 * Shows / hides the conversation panel from the chat header.
 *
 * Two presentations, one control (data-testid="conversations-toggle"):
 *   - Desktop (>= lg): a compact ICON button. The panel's own heading already
 *     reads "Conversations" right beside it, so a text label here looked like a
 *     duplicate (2026-10-17 review). Its name and tooltip say what pressing it
 *     does: "Hide conversations" / "Show conversations".
 *   - Phone (< lg): the panel is a sheet that starts closed, so a bare icon is
 *     too hidden — it keeps a VISIBLE "Conversations" label. A different icon
 *     from the shell's hamburger, so it never reads as a second app menu. Its
 *     accessible name still contains the visible text (WCAG 2.5.3).
 */
import { ChatsIcon, SidebarSimpleIcon } from '@phosphor-icons/react';
import type { Ref } from 'react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@andavo/web-ui';

export function ConversationsToggle({
  buttonRef,
  expanded,
  isWide,
  onToggle,
}: {
  /** The page keeps this so focus can return here when the sheet closes. */
  buttonRef?: Ref<HTMLButtonElement>;
  /** Whether the panel is currently shown (column >= lg, sheet < lg). */
  expanded: boolean;
  /** >= lg: icon-only presentation. */
  isWide: boolean;
  onToggle: () => void;
}) {
  const label = expanded ? 'Hide conversations' : 'Show conversations';

  const button = (
    <Button
      aria-controls="conversation-sidebar"
      aria-expanded={expanded}
      aria-label={label}
      className={
        isWide
          ? 'shrink-0 text-content-secondary'
          : 'shrink-0 gap-2 text-content-secondary'
      }
      data-testid="conversations-toggle"
      onClick={onToggle}
      ref={buttonRef}
      size={isWide ? 'icon' : 'sm'}
      variant="ghost"
    >
      {isWide ? (
        <SidebarSimpleIcon aria-hidden="true" className="size-icon-sm" />
      ) : (
        <>
          <ChatsIcon aria-hidden="true" className="size-icon-sm" />
          <span>Conversations</span>
        </>
      )}
    </Button>
  );

  if (!isWide) return button;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="bottom">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
